import { BitrixInfoItemConfig, Scenario as ScenarioType, ScenarioItem } from "api/dialog";
import { DialogHistoryItem } from "components/DialogBox";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { evalExprStr } from "utils/evaluator";
import { getWidgetState, usePersistentState } from "utils/useStorage";
import {
  assert,
  clone,
  contextReplace,
  emitEvent,
  replaceLink,
  useTimeout,
} from "utils/utils";
import i18n from "i18next";

declare const window: Window & { top?: Window & { bitrixWidget?: any; BX: any } };

export const initialDialog = (
  keyboardIndex: number,
  items: ScenarioItem[]
): DialogHistoryItem[] => {
  return (keyboardIndex !== -1 ? items.slice(0, keyboardIndex) : items).map((item) => ({
    dialogBlockType: "bot",
    ...item.dialogBlock,
  })) as DialogHistoryItem[];
};

export type Stack = {
  branch: string;
  stack: {
    dialogIndex: number;
    state: number;
    context: Context;
    callStack: { branch: string; scenarioState: number }[];
  }[];
}[];

export const isInitialStack = (stack: Stack) =>
  stack.length === 1 && stack[0].stack.length === 0;
export const isCurrentStackEmpty = (stack: Stack) =>
  stack[stack.length - 1].stack.length === 0;

export type Context = Record<string, any>;

export type PersistentDialogHistoryItem = { id: string } & DialogHistoryItem;
export const useDialog = (name: string, initial: DialogHistoryItem[] = []) => {
  const [dialog, setDialog] = usePersistentState<PersistentDialogHistoryItem[]>(
    name,
    initial.map((x) => ({ ...x, id: crypto.randomUUID() }))
  );
  return {
    dialog,
    send: (...messages: DialogHistoryItem[]) =>
      setDialog((dialog) => [
        ...dialog,
        ...messages.map((x) => ({ ...x, id: crypto.randomUUID() })),
      ]),
    backTo: (dialogIndex: number) => setDialog(dialog.slice(0, dialogIndex)),
  };
};

type BitrixMessage = {
  chat: Record<string, any>;
  chatId: number;
  dialogId: string;
  files: any[];
  lines: null;
  message: {
    chatId: number;
    date: string;
    id: number;
    params: any[];
    prevId: number;
    recipientId: string;
    senderId: number;
    system: "N" | "Y";
    text: string;
    textOriginal: string;
  };
  notify: boolean;
  userBlockChat: Record<string, any>;
  userInChat: Record<string, any[]>;
  users: Record<string, any>;
};
type BitrixConnection = {
  state: "waitingOperator" | "session" | "pending";
  send: (msg: string) => void;
  setData: (data: any[]) => void;
};

let chatIsSetUp = false;
let chatIsSetUpEvent = "chatIsSetUp";
let chatOperator: { avatar?: string } | undefined;
let chatMessageCallbacks = new Set<() => void>();

export const useBitrixChat = (config: BitrixInfoItemConfig[][]) => {
  const bitrixConnection = useRef<BitrixConnection | null>(null);
  const [showDialog, setShowDialog] = usePersistentState<boolean>(
    "showBitrixDialog",
    false
  );
  const { dialog, send: _send } = useDialog("bitrixDialog");
  //Ініціалізація реєстраційних даних
  const getUserRegisterData = (
    data: any,
    id: string = data.name + data.lastName + data.email
  ) => {
    return {
      ...data,
      //Заповнення хеш, це обов'язковий параметр, має бути унікальним для користувача в форматі md5, є ідентифікатором клієнта в бітрікс
      // @ts-ignore
      hash: CryptoJS.MD5(id).toString(),
      gender: "M",
      position: "Клієнт",
    };
  };
  const setUserData = useCallback(
    (data: any) => {
      if (!bitrixConnection.current) return;
      if (bitrixConnection.current.state === "pending") return;

      //заповнення додаткових даних що передаватимуться працівнику в чат
      bitrixConnection.current.setData(
        config.map((col) => ({
          GRID: col.flatMap((item) => {
            // if didn't find or is empty - don't show taht field
            try {
              const value = evalExprStr(item.value, data);
              if (!value) return [];
              return [{ NAME: item.name, VALUE: value, DISPLAY: "COLUMN" }];
            } catch {
              return [];
            }
          }),
        }))
      );
    },
    [config]
  );
  const executeSubscriptions = () => [...chatMessageCallbacks].forEach((f) => f());
  const send: typeof _send = (...args) => {
    _send(...args);
    executeSubscriptions();
  };

  useEffect(() => {
    if (!chatIsSetUp) {
      send({
        dialogBlockType: "bot",
        type: "text",
        text: i18n.t("widget.bitrix.waiting"),
      });
      chatIsSetUp = true;
      const widget = window.top.bitrixWidget;
      const setupBitrixChat = (widget: any) => {
        // @ts-ignore
        const persona = genPerson();
        // console.log("setting up widget's bitrix chat");

        window.top.document.addEventListener(
          "bitrixSessionStart",
          function ({ detail: e }: any) {
            // console.log("widget's bitrix chat", e);
            if (!bitrixConnection.current) return;
            send({
              dialogBlockType: "bot",
              type: "text",
              text: i18n.t("widget.bitrix.operator.waiting"),
            });
            bitrixConnection.current.state = "session";
          }
        );
        window.top.document.addEventListener(
          "bitrixSessionFinish",
          function ({ detail: e }: any) {
            // console.log("widget's bitrix chat", e);
            if (!bitrixConnection.current) return;
            send({
              dialogBlockType: "bot",
              type: "text",
              text: i18n.t("widget.bitrix.operator.left"),
            });
            bitrixConnection.current.state = "waitingOperator";
          }
        );
        window.top.document.addEventListener(
          "bitrixSessionOperatorChange",
          function ({ detail: e }: any) {
            // console.log("widget's bitrix chat", e);
            if (!bitrixConnection.current) return;
            if (!!chatOperator)
              send({
                dialogBlockType: "bot",
                type: "text",
                text: i18n.t("widget.bitrix.operator.changed"),
              });
            else
              send({
                dialogBlockType: "bot",
                type: "text",
                text: i18n.t("widget.bitrix.operator.join"),
              });
            chatOperator = { avatar: e.avatar };
          }
        );
        window.top.document.addEventListener(
          "bitrixOperatorMessage",
          function ({ detail: e }: any) {
            // console.log("widget's bitrix chat", e);
            send({
              dialogBlockType: "operator",
              type: "text",
              text: e.message.text,
              avatar: chatOperator?.avatar,
            });
          }
        );
        bitrixConnection.current = {
          state: "pending",
          send: (msg: string) =>
            emitEvent("bitrixSendMessage", { msg }, window.top.document),
          setData: (data: any[]) => {
            if (data.length > 0) widget.setCustomData(data);
          },
        };

        //Встановлення реєстраційних даних
        widget.setUserRegisterData(
          getUserRegisterData({
            ...persona,
            email: persona.lastName + persona.name + "@gmail.com",
          })
        );

        const el = window.top?.document.getElementsByClassName(
          "b24-widget-button-wrapper"
        )[0].parentElement?.parentElement;
        const el2 = el?.previousSibling;

        if (el) (el as HTMLElement).style.display = "none";
        if (el2) (el2 as HTMLElement).style.display = "none";

        widget.open();
        const event = new Event(chatIsSetUpEvent);
        window.dispatchEvent(event);

        window.top?.document
          .querySelector("body")
          ?.classList.remove("bx-livechat-mobile-state");
      };

      if (widget) setupBitrixChat(widget);
      else
        window.top?.addEventListener("onBitrixLiveChat", function (event) {
          // @ts-ignore
          const widget = event.detail.widget;
          setupBitrixChat(widget);
        });
    }
  });

  return {
    dialog,
    showDialog,
    chatState: bitrixConnection.current?.state ?? "uninitialized",
    toggle: () => setShowDialog(!showDialog),
    send: (text: string) => {
      if (!bitrixConnection.current) return;
      if (bitrixConnection.current.state === "pending") return;
      bitrixConnection.current?.send(text);
      send({ dialogBlockType: "user", type: "text", text });
    },
    onMessage: (callback: () => void) =>
      (chatMessageCallbacks = chatMessageCallbacks.add(callback)),
    cancelOnMessage: (callback: () => void) => chatMessageCallbacks.delete(callback),
    setUserData,
    start: (message = "Start Dialog") => {
      // console.log(bitrixConnection);
      const f = () => {
        assert(bitrixConnection.current);
        if (bitrixConnection.current.state !== "pending") return;
        bitrixConnection.current.send(message);
        bitrixConnection.current.state = "waitingOperator";
        // console.log("started");
      };

      if (!bitrixConnection.current) {
        window.addEventListener(chatIsSetUpEvent, f);
      } else f();
    },
  };
};

const traverse = (branches: ScenarioType) => {
  const memo: Record<string, number> = {};
  const res = (branch: string, path: string[] = []): number => {
    const item = memo[branch];
    if (item !== undefined) return item;

    if (path.includes(branch)) {
      // console.warn("scenario infinite loop", branch, path);
      return Number.POSITIVE_INFINITY;
    }

    const getLen = (_branches: string[]) =>
      _branches.reduce(
        (acc, _branch) => Math.max(acc, res(_branch, [...path, branch])),
        0
      );

    const len =
      branches[branch]?.reduce((acc, item) => {
        const { dialogBlock } = item;
        if (dialogBlock?.blockType === "interactive" && dialogBlock.type === "branch") {
          return acc + getLen(dialogBlock.matchers.map(({ next }) => next));
        }
        if (
          dialogBlock?.blockType === "interactive" &&
          "branching" in dialogBlock &&
          dialogBlock.branching
        ) {
          const { default: _default, next } = dialogBlock.branching;
          const branches = [
            _default,
            ...(typeof next === "string"
              ? next
              : (next ?? []).map(({ branch }) => branch)),
          ];
          return acc + getLen(branches);
        }
        return acc + 1;
      }, 0) ?? 0;

    memo[branch] = len;

    return len;
  };
  return res;
};

export type InitialState = {
  state?: number;
  branch?: string;
  context?: Context;
  data?: Context;
};

// TODO: add block batching not only to content blocks, but also "non interactive" ones
// TODO: add context proxies, so that its easy to add "fields" that access remote resources.
// for example "ctx.localizations.${key}" could access `key` localization from i18next store
// or accessing "ctx.authed" could try to fetch user if field is not set.
export const useScenario = (
  branches: ScenarioType,
  timeout: { delay: number; callback: (context: Context) => void },
  initialState: InitialState = {}
) => {
  const [data, setData] = usePersistentState<Context>("data", initialState.context ?? {});
  const [context, setContext] = usePersistentState<Context>("context", {
    now: new Date(),
    null: null,
    ...(initialState.context ?? {}),
  });
  const [branch, setBranch] = usePersistentState(
    "branch",
    initialState.branch ?? "initial"
  );
  const [scenarioState, setScenarioState] = usePersistentState(
    "scenarioState",
    initialState.state ?? 0
  );
  const [callStack, setCallStack] = usePersistentState<
    { branch: string; scenarioState: number }[]
  >("callStack", []);
  const {
    dialog,
    send: _send,
    backTo,
  } = useDialog("dialog", initialDialog(scenarioState, branches[branch]));
  const [stack, setStack] = usePersistentState<Stack>("stack", [
    {
      branch,
      stack: [] as Stack[number]["stack"],
    },
  ]);
  const _traverse = useMemo(() => traverse(branches), [branches]);
  const progress = useMemo(
    () =>
      Number.POSITIVE_INFINITY === _traverse("initial") ||
      Number.POSITIVE_INFINITY === _traverse(branch)
        ? 0
        : 1 + (scenarioState - _traverse(branch)) / _traverse("initial"),
    // [_traverse, dialog] // removing, because decided to hide progress bar
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const state = {
    stack,
    dialog,
    scenarioState,
    branch,
    progress,
    context,
    data,
  };
  const resetTimeout = useTimeout(timeout.delay, () =>
    timeout.callback({ ...context, data })
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => resetTimeout(), [scenarioState, branch]);
  const send = (context: Record<string, unknown>, ...messages: DialogHistoryItem[]) => {
    if (messages.length === 0) return;
    return _send(
      ...messages.map((message) => {
        if (!("text" in message)) return message;
        const replaced = replaceLink(contextReplace(message.text ?? "", context));
        return { ...message, ...replaced };
      })
    );
  };
  const back = () => {
    let empty = false;
    while (isCurrentStackEmpty(stack)) {
      empty = true;
      stack.pop();
    }

    const top = stack[stack.length - 1];
    const { state, dialogIndex, context, callStack } = (top.stack.pop() ??
      {}) as typeof top.stack[0];

    if (!empty) {
    } else setBranch(top.branch);
    if (stack.length === 1) {
      if (top.stack.length < 1) emitEvent("widgetCloseClick");
    }

    setScenarioState(state);
    setContext(context);
    setStack([...stack]);
    setCallStack([...callStack]);
    backTo(dialogIndex);
  };
  const switchBranch = (nextBranch: string) => {
    setCallStack([...callStack, { branch, scenarioState }]);
    setBranch(nextBranch);
    setScenarioState(0);
    setStack((stack) => [...stack, { branch: nextBranch, stack: [] }]);
  };
  const pushStack = (shouldPushStack: boolean) => {
    if (!shouldPushStack) return;
    const contextClone = clone(context);
    const callStackClone = clone(callStack);

    setStack((_stack) => {
      const dialogIndex = getWidgetState().dialog.length;
      const stack = clone(_stack);
      const { stack: inner, ...rest } = stack[stack.length - 1];

      stack[stack.length - 1] = {
        stack: [
          ...inner,
          {
            dialogIndex,
            state: scenarioState,
            context: contextClone,
            callStack: callStackClone,
          },
        ],
        ...rest,
      };

      return stack;
    });
  };

  const next = (shouldPushStack: boolean, nextBranch?: string): boolean => {
    if (nextBranch === "back") {
      back();
      return true;
    }
    if (nextBranch === "continue") {
      next(shouldPushStack, undefined);
      return true;
    }

    pushStack(shouldPushStack);
    if (nextBranch) {
      switchBranch(nextBranch);
      return true;
    }

    if (branches[branch][scenarioState + 1]) setScenarioState(scenarioState + 1);
    else {
      while (true) {
        const top = callStack.pop();

        if (top) {
          const { branch, scenarioState } = top;

          if (branches[branch][scenarioState + 1]) {
            setScenarioState(scenarioState + 1);
            setBranch(branch);
            setStack((stack) => [...stack, { branch, stack: [] }]);
            setCallStack([...callStack]);
            return true;
          }
        } else break;
      }
    }

    return !!branches[branch][scenarioState + 1];
  };

  const clearCallStack = () => {
    setCallStack([]);
  };

  return {
    state,
    clearCallStack,
    setContext,
    setData,
    back,
    pushStack,
    showMessage: send,
    nextStep: next,
  } as const;
};
